(() => {
  'use strict';

  const rootSelector = '#judgeme_all_reviews_page';
  const translateReviewText = 'Translate review to English';
  const translatingReviewText = 'Translating...';
  const showOriginalPrefix = 'Show original';
  const retryTranslationText = 'Retry';
  const maxTranslationAttempts = 2;
  const translationRetryDelayMs = 2500;
  const reviewBatchSize = 8;
  const judgeMeButtonMode = 'button_click';
  const viewMoreText = 'View more reviews';
  const loadingMoreText = 'Loading reviews...';
  const remoteLoadTimeoutMs = 12000;
  const initialWidgetRevealTimeoutMs = 5000;
  const productCatalogPath = '/products.json?limit=250';
  const productCatalogTimeoutMs = 4000;
  const productImageSize = 64;

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function getSelectedSortLabel(sortDropdown) {
    if (!sortDropdown) return '';

    const selectedOption =
      sortDropdown.selectedOptions?.[0] ||
      sortDropdown.options?.[sortDropdown.selectedIndex];
    return normalizeText(selectedOption?.textContent);
  }

  function isTranslatedControlText(value) {
    const text = normalizeText(value);
    return text === showOriginalPrefix || text.startsWith(`${showOriginalPrefix} (`);
  }

  function isTranslationTriggerText(value) {
    const text = normalizeText(value);
    return text === translateReviewText || text === retryTranslationText;
  }

  function formatReviewerName(value) {
    const name = normalizeText(value);
    if (!name || /^anonymous$/i.test(name)) return name || 'Anonymous';

    const parts = name.split(' ');
    if (parts.length < 2) return name;

    const firstName = parts[0];
    const suffixPattern = /^(?:jr\.?|sr\.?|ii|iii|iv|v)$/i;
    let lastNameIndex = parts.length - 1;

    while (lastNameIndex > 0 && suffixPattern.test(parts[lastNameIndex])) {
      lastNameIndex -= 1;
    }

    const lastName = parts[lastNameIndex];
    const lastInitial = Array.from(lastName).find((character) => /\p{L}/u.test(character));

    if (!lastInitial) return firstName;
    return `${firstName} ${lastInitial.toLocaleUpperCase('en-US')}.`;
  }

  function normalizeProductUrl(value, currentOrigin) {
    try {
      const currentUrl = new URL(currentOrigin);
      const productUrl = new URL(value, currentUrl);
      const productHost = productUrl.hostname.toLowerCase().replace(/^www\./, '');
      const currentHost = currentUrl.hostname.toLowerCase().replace(/^www\./, '');
      const normalizedPath = productUrl.pathname.replace(/\/+$/, '');

      if (productHost !== 'nordweg.us' && productHost !== currentHost) return null;
      if (!/^\/products\/[a-z0-9][a-z0-9-]*$/i.test(normalizedPath)) return null;

      return `${currentUrl.origin}${normalizedPath}`;
    } catch {
      return null;
    }
  }

  function normalizeProductImage(value, currentOrigin) {
    const source = normalizeText(value?.src);
    if (!source) return null;

    try {
      const currentUrl = new URL(currentOrigin);
      const imageUrl = new URL(source, currentUrl);
      const imageHost = imageUrl.hostname.toLowerCase();
      const currentHost = currentUrl.hostname.toLowerCase();
      if (imageUrl.protocol !== 'https:') return null;
      if (imageHost !== 'cdn.shopify.com' && imageHost !== currentHost) return null;

      return {
        src: imageUrl.toString(),
        alt: normalizeText(value.alt),
        width: Math.max(0, Math.floor(Number(value.width) || 0)),
        height: Math.max(0, Math.floor(Number(value.height) || 0)),
      };
    } catch {
      return null;
    }
  }

  function extractProductModelCode(...values) {
    for (const value of values) {
      const match = normalizeText(value)
        .toUpperCase()
        .match(/\bNW[A-Z]?\d{2,4}[A-Z]?\b/);
      if (match) return match[0];
    }
    return '';
  }

  function normalizeVariantKey(value) {
    const primaryValue = normalizeText(value).split('/')[0].trim();
    const normalized = primaryValue
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
    const aliases = {
      cafe: 'coffee',
      caramelo: 'caramel',
      preto: 'black',
    };
    return aliases[normalized] || normalized;
  }

  function createProductCatalog(products, currentOrigin) {
    const byHandle = new Map();
    const byModelCode = new Map();
    const ambiguousModelCodes = new Set();

    (Array.isArray(products) ? products : []).forEach((product) => {
      const title = normalizeText(product?.title);
      const handle = normalizeText(product?.handle).toLowerCase();
      const url = normalizeProductUrl(`/products/${handle}`, currentOrigin);
      if (!title || !url) return;

      const variants = (Array.isArray(product.variants) ? product.variants : [])
        .map((variant) => {
          const variantTitle = normalizeText(variant?.title);
          const modelCode = extractProductModelCode(variant?.sku);
          return {
            title: variantTitle,
            key: normalizeVariantKey(variantTitle),
            modelCode,
            available: Boolean(variant?.available),
            image: normalizeProductImage(variant?.featured_image, currentOrigin),
          };
        })
        .filter((variant) => variant.title || variant.modelCode || variant.image);
      const record = {
        title,
        handle,
        url,
        image: normalizeProductImage(product?.images?.[0], currentOrigin),
        variants,
      };

      byHandle.set(handle, record);
      new Set(variants.map((variant) => variant.modelCode).filter(Boolean)).forEach(
        (modelCode) => {
          if (ambiguousModelCodes.has(modelCode)) return;
          const existingRecord = byModelCode.get(modelCode);
          if (existingRecord && existingRecord.handle !== handle) {
            byModelCode.delete(modelCode);
            ambiguousModelCodes.add(modelCode);
            return;
          }
          byModelCode.set(modelCode, record);
        }
      );
    });

    return { byHandle, byModelCode };
  }

  function resolveReviewProduct(source, catalog, currentOrigin) {
    if (!(catalog?.byHandle instanceof Map) || !(catalog?.byModelCode instanceof Map)) {
      return null;
    }

    const sourceUrl = normalizeText(source?.sourceUrl);
    const currentProductUrl = normalizeProductUrl(sourceUrl, currentOrigin);
    let product = null;

    if (currentProductUrl) {
      const currentHandle = new URL(currentProductUrl).pathname.split('/').filter(Boolean)[1];
      product = catalog.byHandle.get(currentHandle) || null;
    }

    if (!product) {
      const modelCode = extractProductModelCode(source?.sourceName, sourceUrl);
      product = catalog.byModelCode.get(modelCode) || null;
    }
    if (!product) return null;

    const variantKey = normalizeVariantKey(source?.variantName);
    let variant = null;
    if (variantKey) {
      const matchingVariants = product.variants.filter(
        (candidate) => candidate.key === variantKey
      );
      if (matchingVariants.length !== 1) return null;
      [variant] = matchingVariants;
    } else if (product.variants.length === 1) {
      variant = product.variants[0];
    }

    const image = variant?.image || product.image;
    if (!image) return null;

    return {
      title: product.title,
      handle: product.handle,
      url: product.url,
      variantTitle:
        variant?.title && !/^default title$/i.test(variant.title) ? variant.title : '',
      available: variant
        ? variant.available
        : product.variants.some((candidate) => candidate.available),
      image: { ...image },
    };
  }

  function isEnglishSourceLanguage(value) {
    const language = normalizeText(value).toLowerCase();
    return language === 'en' || language.startsWith('en-');
  }

  function buildReviewHeading(existingTitle, productName, sourceLanguage) {
    const normalizedProductName = normalizeText(productName);
    if (normalizedProductName && !/^customer review$/i.test(normalizedProductName)) {
      return /\breview$/i.test(normalizedProductName)
        ? normalizedProductName
        : `${normalizedProductName} Review`;
    }

    const normalizedTitle = normalizeText(existingTitle);
    if (/^customer review$/i.test(normalizedTitle)) return '';
    if (isEnglishSourceLanguage(sourceLanguage) && normalizedTitle) return normalizedTitle;
    return '';
  }

  function getNextReviewLimit(currentLimit, totalReviews, batchSize = reviewBatchSize) {
    const total = Math.max(0, Math.floor(Number(totalReviews) || 0));
    const current = Math.max(0, Math.floor(Number(currentLimit) || 0));
    const increment = Math.max(1, Math.floor(Number(batchSize) || reviewBatchSize));

    return Math.min(total, current > 0 ? current + increment : increment);
  }

  function shouldShowPagination(
    widgetIsEnhanced,
    hasLocalReviews,
    hasRemoteReviews,
    isLoading
  ) {
    return Boolean(
      widgetIsEnhanced && (hasLocalReviews || hasRemoteReviews || isLoading)
    );
  }

  function shouldRevealReviewsWidget(structureStable, translationPending, force = false) {
    return Boolean(force || (structureStable && !translationPending));
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    if (typeof module !== 'undefined') {
      module.exports = {
        buildReviewHeading,
        createProductCatalog,
        formatReviewerName,
        getNextReviewLimit,
        getSelectedSortLabel,
        isEnglishSourceLanguage,
        isTranslatedControlText,
        isTranslationTriggerText,
        normalizeProductUrl,
        resolveReviewProduct,
        shouldRevealReviewsWidget,
        shouldShowPagination,
      };
    }
    return;
  }

  if (window.jdgmSettings) {
    window.jdgmSettings.all_reviews_page_load_reviews_on = judgeMeButtonMode;
    window.jdgmSettings.widget_translate_review_content_method = 'manual';
  }

  const translationAttempts = new WeakMap();
  const reviewBatchLimits = new WeakMap();
  let activeTranslationButton = null;
  let enhancementScheduled = false;
  let productCatalog = null;
  let productCatalogState = 'idle';
  let productCatalogTimer = null;
  let remoteLoadRequest = null;
  let remoteLoadTimer = null;
  let translationRetryTimer = null;
  let visibleTranslationBatchActive = false;
  let initialWidgetRevealTimer = null;

  function formatReviewerNames(root) {
    root.querySelectorAll('.jdgm-rev__author').forEach((author) => {
      const formattedName = formatReviewerName(author.textContent);
      if (formattedName && normalizeText(author.textContent) !== formattedName) {
        author.textContent = formattedName;
      }
      author.dataset.nwReviewerName = 'formatted';
    });
  }

  function syncSortControlLabels(root) {
    root.querySelectorAll('.jdgm-row-actions .jdgm-sort-dropdown').forEach((sortDropdown) => {
      const wrapper = sortDropdown.closest('.jdgm-sort-dropdown-wrapper');
      if (!wrapper) return;

      const accessibleLabel =
        normalizeText(wrapper.querySelector('.jdgm-sort-dropdown-label')?.textContent) ||
        'Sort by';
      sortDropdown.setAttribute('aria-label', accessibleLabel);

      const selectedLabel = getSelectedSortLabel(sortDropdown);
      if (selectedLabel) {
        wrapper.dataset.nwSortLabel = selectedLabel;
      } else {
        delete wrapper.dataset.nwSortLabel;
      }
    });
  }

  function getMeaningfulReviewCopy(review) {
    const reviewBody = review.querySelector('.jdgm-rev__body');
    if (!reviewBody) return '';

    const reviewBodyCopy = reviewBody.cloneNode(true);
    reviewBodyCopy
      .querySelectorAll('.jdgm-rev__body-read-more, button')
      .forEach((control) => control.remove());
    return normalizeText(reviewBodyCopy.textContent);
  }

  function prepareReviewPresentation(root) {
    root.querySelectorAll('.jdgm-rev').forEach((review) => {
      const sourceLanguage = normalizeText(review.dataset.reviewLanguage);
      const hasNonEnglishSource =
        Boolean(sourceLanguage) &&
        sourceLanguage !== '_blank' &&
        !isEnglishSourceLanguage(sourceLanguage);
      const reviewCopy = getMeaningfulReviewCopy(review);

      review.classList.toggle('nw-review--non-english-source', hasNonEnglishSource);
      review.querySelectorAll('.jdgm-row-extra').forEach((metadataRow) => {
        metadataRow.hidden = !normalizeText(metadataRow.textContent);
      });
      review.hidden = !reviewCopy;
      if (!reviewCopy) return;

      const reviewTitle = review.querySelector('.jdgm-rev__title');
      const linkedProduct = review.querySelector(
        '.nw-review-product-link:not([hidden]) .jdgm-rev__prod-link[href]'
      );
      const isTranslated = review.classList.contains('nw-review--translated');

      if (reviewTitle) {
        const reviewHeading = buildReviewHeading(
          reviewTitle.textContent,
          linkedProduct?.textContent,
          isTranslated ? 'en' : sourceLanguage
        );
        if (normalizeText(reviewTitle.textContent) !== reviewHeading) {
          reviewTitle.textContent = reviewHeading;
        }
        reviewTitle.dataset.nwReviewHeading = 'formatted';
        reviewTitle.setAttribute('lang', 'en');
      }

      const translationPending =
        hasNonEnglishSource &&
        !isTranslated &&
        !review.classList.contains('nw-review--translation-failed');
      review.classList.toggle('nw-review--translation-pending', translationPending);
    });
  }

  function expandTruncatedReviews(root) {
    root
      .querySelectorAll(
        '.jdgm-rev:not(.nw-review--batch-hidden) .jdgm-rev__body.is-truncated .jdgm-rev__body-read-more:not([data-nw-review-expand-requested])'
      )
      .forEach((expandControl) => {
        expandControl.dataset.nwReviewExpandRequested = 'true';
        expandControl.click();
      });
  }

  function getSizedProductImageUrl(value, width) {
    try {
      const imageUrl = new URL(value);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.toString();
    } catch {
      return '';
    }
  }

  function getReviewVariantName(review) {
    const variantWrapper = review?.querySelector('.jdgm-rev__prod-variant-wrapper');
    if (!variantWrapper) return '';

    const explicitValues = Array.from(variantWrapper.children)
      .filter((element) => !element.classList.contains('jdgm-rev__variant-label'))
      .map((element) => normalizeText(element.textContent))
      .filter(Boolean);
    return normalizeText(explicitValues.join(' ')) ||
      normalizeText(variantWrapper.textContent).replace(/^variant:\s*/i, '');
  }

  function createProductCardSpan(className, text) {
    const span = document.createElement('span');
    span.className = className;
    if (text) span.textContent = text;
    return span;
  }

  function resetProductCard(productRow, productLink, variantWrapper, sourceName) {
    if (productLink.classList.contains('nw-review-product-card__link')) {
      productLink.replaceChildren(document.createTextNode(sourceName));
      productLink.classList.remove('nw-review-product-card__link');
    }
    productLink.removeAttribute('href');
    productLink.removeAttribute('target');
    productLink.removeAttribute('aria-disabled');
    productLink.removeAttribute('aria-label');
    productRow.classList.remove('nw-review-product-card');
    delete productRow.dataset.nwProductCardKey;
    if (!productRow.hidden) productRow.hidden = true;

    if (variantWrapper?.dataset.nwProductCardVariant === 'true') {
      variantWrapper.hidden = false;
      delete variantWrapper.dataset.nwProductCardVariant;
    }
  }

  function renderProductCard(productRow, productLink, review, resolvedProduct) {
    const variantWrapper = review.querySelector('.jdgm-rev__prod-variant-wrapper');
    const cardKey = `${resolvedProduct.handle}:${resolvedProduct.variantTitle}`;
    if (
      productRow.dataset.nwProductCardKey === cardKey &&
      productLink.classList.contains('nw-review-product-card__link')
    ) {
      if (productRow.hidden) productRow.hidden = false;
      if (variantWrapper && !variantWrapper.hidden) variantWrapper.hidden = true;
      return;
    }

    const media = createProductCardSpan('nw-review-product-card__media');
    const image = document.createElement('img');
    image.className = 'nw-review-product-card__image';
    image.src = getSizedProductImageUrl(
      resolvedProduct.image.src,
      productImageSize * 2
    );
    image.srcset = [productImageSize, productImageSize * 2]
      .map(
        (width) =>
          `${getSizedProductImageUrl(resolvedProduct.image.src, width)} ${width}w`
      )
      .join(', ');
    image.sizes = `${productImageSize}px`;
    image.width = productImageSize;
    image.height = productImageSize;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.setAttribute('fetchpriority', 'low');
    media.append(image);

    const copy = createProductCardSpan('nw-review-product-card__copy');
    copy.append(
      createProductCardSpan('nw-review-product-card__eyebrow', 'Reviewed product'),
      createProductCardSpan('nw-review-product-card__title', resolvedProduct.title)
    );
    if (resolvedProduct.variantTitle) {
      copy.append(
        createProductCardSpan(
          'nw-review-product-card__variant',
          resolvedProduct.variantTitle
        )
      );
    }
    if (!resolvedProduct.available) {
      copy.append(
        createProductCardSpan('nw-review-product-card__status', 'Sold out')
      );
    }

    const arrow = createProductCardSpan('nw-review-product-card__arrow', '\u2192');
    arrow.setAttribute('aria-hidden', 'true');
    productLink.replaceChildren(media, copy, arrow);
    productLink.classList.add('nw-review-product-card__link');
    productLink.href = resolvedProduct.url;
    productLink.removeAttribute('target');
    productLink.removeAttribute('aria-disabled');
    productLink.setAttribute(
      'aria-label',
      `Shop ${resolvedProduct.title}${
        resolvedProduct.variantTitle ? `, ${resolvedProduct.variantTitle}` : ''
      }${resolvedProduct.available ? '' : ', sold out'}`
    );

    productRow.hidden = false;
    productRow.classList.add('nw-review-product-card');
    productRow.dataset.nwProductCardKey = cardKey;
    if (variantWrapper) {
      variantWrapper.hidden = true;
      variantWrapper.dataset.nwProductCardVariant = 'true';
    }
  }

  function enhanceProductCards(root) {
    root.querySelectorAll('.jdgm-rev__prod-link').forEach((productLink) => {
      const productRow = productLink.closest('.jdgm-row-product');
      if (!productRow) return;

      const review = productLink.closest('.jdgm-rev');
      const reviewContent = review?.querySelector('.jdgm-rev__content');
      const variantWrapper = review?.querySelector('.jdgm-rev__prod-variant-wrapper');
      const sourceUrl =
        productLink.dataset.nwProductSource || normalizeText(productLink.getAttribute('href'));
      if (sourceUrl && !productLink.dataset.nwProductSource) {
        productLink.dataset.nwProductSource = sourceUrl;
      }
      const sourceName =
        productLink.dataset.nwProductNameSource || normalizeText(productLink.textContent);
      if (sourceName && !productLink.dataset.nwProductNameSource) {
        productLink.dataset.nwProductNameSource = sourceName;
      }

      const resolvedProduct = resolveReviewProduct(
        {
          sourceUrl,
          sourceName,
          variantName: getReviewVariantName(review),
        },
        productCatalog,
        window.location.origin
      );
      if (!resolvedProduct) {
        resetProductCard(productRow, productLink, variantWrapper, sourceName);
        return;
      }

      renderProductCard(productRow, productLink, review, resolvedProduct);
      if (reviewContent && reviewContent.nextElementSibling !== productRow) {
        reviewContent.insertAdjacentElement('afterend', productRow);
      }
    });
  }

  function clearProductCatalogTimer() {
    if (productCatalogTimer === null) return;
    window.clearTimeout(productCatalogTimer);
    productCatalogTimer = null;
  }

  function loadProductCatalog(root) {
    if (productCatalogState !== 'idle') return;
    productCatalogState = 'loading';
    const controller = new AbortController();
    productCatalogTimer = window.setTimeout(
      () => controller.abort(),
      productCatalogTimeoutMs
    );

    window
      .fetch(new URL(productCatalogPath, window.location.origin).toString(), {
        cache: 'force-cache',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      .then((response) => {
        if (!response.ok) throw new Error('Product catalog request failed.');
        return response.json();
      })
      .then((payload) => {
        productCatalog = createProductCatalog(payload?.products, window.location.origin);
        productCatalogState = 'ready';
      })
      .catch(() => {
        productCatalog = null;
        productCatalogState = 'failed';
      })
      .finally(() => {
        clearProductCatalogTimer();
        scheduleEnhancement(root);
      });
  }

  function getReviewLists(root) {
    return Array.from(
      root.querySelectorAll('.jdgm-all-reviews__body, .jdgm-shop-reviews__body')
    );
  }

  function getActiveReviewList(root) {
    const reviewLists = getReviewLists(root);
    return (
      reviewLists.find(
        (reviewList) =>
          !reviewList.hidden && window.getComputedStyle(reviewList).display !== 'none'
      ) ||
      reviewLists[0] ||
      null
    );
  }

  function getRenderableReviews(reviewList) {
    if (!reviewList) return [];
    return Array.from(reviewList.querySelectorAll('.jdgm-rev')).filter(
      (review) => !review.hidden
    );
  }

  function getNativeLoadMoreButton(root) {
    const nativeButton = root.querySelector('.jdgm-all-reviews-page__load-more');
    if (!nativeButton) return null;

    const nativeWrapper = nativeButton.closest(
      '.jdgm-all-reviews-page__load-more-wrapper'
    );
    const nativeFooter = nativeButton.closest('.jdgm-all-reviews__footer');
    if (
      nativeWrapper?.classList.contains('jdgm-hidden') ||
      nativeFooter?.classList.contains('jdgm-hidden')
    ) {
      return null;
    }

    return nativeButton;
  }

  function clearRemoteLoadRequest() {
    if (remoteLoadTimer !== null) {
      window.clearTimeout(remoteLoadTimer);
      remoteLoadTimer = null;
    }
    remoteLoadRequest = null;
  }

  function setTextIfChanged(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }

  function updatePaginationControl(root, activeReviewList) {
    const pagination = root.querySelector('[data-nw-reviews-pagination]');
    const viewMoreButton = pagination?.querySelector('[data-nw-reviews-view-more]');
    const status = pagination?.querySelector('[data-nw-reviews-pagination-status]');
    if (!pagination || !viewMoreButton || !activeReviewList) return;

    const reviews = getRenderableReviews(activeReviewList);
    const limit = reviewBatchLimits.get(activeReviewList) || reviewBatchSize;
    const shownCount = Math.min(limit, reviews.length);
    const hasLocalReviews = reviews.length > limit;
    const isLoading = remoteLoadRequest?.reviewList === activeReviewList;
    const hasRemoteReviews = Boolean(getNativeLoadMoreButton(root));
    const widgetIsEnhanced =
      root.querySelector('.jdgm-all-reviews-widget')?.dataset.nwReviewsEnhanced === 'true';
    const canShowMore = shouldShowPagination(
      widgetIsEnhanced,
      hasLocalReviews,
      hasRemoteReviews,
      isLoading
    );

    pagination.hidden = !canShowMore;
    viewMoreButton.disabled = isLoading;
    viewMoreButton.setAttribute('aria-busy', String(isLoading));
    viewMoreButton.setAttribute('aria-controls', activeReviewList.id);
    setTextIfChanged(viewMoreButton, isLoading ? loadingMoreText : viewMoreText);
    setTextIfChanged(
      status,
      canShowMore
        ? `Showing ${shownCount} reviews.`
        : `All ${shownCount} available reviews are shown.`
    );
  }

  function applyReviewBatches(root) {
    getReviewLists(root).forEach((reviewList, listIndex) => {
      if (!reviewList.id) {
        reviewList.id =
          listIndex === 0 ? 'nw-product-review-list' : `nw-shop-review-list-${listIndex}`;
      }
      if (!reviewBatchLimits.has(reviewList)) {
        reviewBatchLimits.set(reviewList, reviewBatchSize);
      }

      const reviews = getRenderableReviews(reviewList);
      const visibleLimit = reviewBatchLimits.get(reviewList);
      let lastVisibleReview = null;

      reviews.forEach((review, reviewIndex) => {
        const isBatchHidden = reviewIndex >= visibleLimit;
        review.classList.toggle('nw-review--batch-hidden', isBatchHidden);
        review.classList.remove('nw-review--batch-tail');

        if (isBatchHidden) {
          review.setAttribute('aria-hidden', 'true');
        } else {
          review.removeAttribute('aria-hidden');
          lastVisibleReview = review;
        }
      });

      lastVisibleReview?.classList.add('nw-review--batch-tail');
    });

    if (
      remoteLoadRequest &&
      getRenderableReviews(remoteLoadRequest.reviewList).length >
        remoteLoadRequest.reviewCount
    ) {
      clearRemoteLoadRequest();
    }

    updatePaginationControl(root, getActiveReviewList(root));
  }

  function resetActiveReviewBatch(root) {
    const activeReviewList = getActiveReviewList(root);
    if (!activeReviewList) return;

    clearRemoteLoadRequest();
    reviewBatchLimits.set(activeReviewList, reviewBatchSize);
    scheduleEnhancement(root);
  }

  function requestNextReviewBatch(root) {
    const activeReviewList = getActiveReviewList(root);
    if (!activeReviewList || remoteLoadRequest) return;

    const reviews = getRenderableReviews(activeReviewList);
    const currentLimit = reviewBatchLimits.get(activeReviewList) || reviewBatchSize;

    if (reviews.length > currentLimit) {
      reviewBatchLimits.set(
        activeReviewList,
        getNextReviewLimit(currentLimit, reviews.length)
      );
      scheduleEnhancement(root);
      return;
    }

    const nativeLoadMoreButton = getNativeLoadMoreButton(root);
    if (!nativeLoadMoreButton) {
      updatePaginationControl(root, activeReviewList);
      return;
    }

    reviewBatchLimits.set(activeReviewList, currentLimit + reviewBatchSize);
    remoteLoadRequest = {
      reviewList: activeReviewList,
      reviewCount: reviews.length,
    };
    updatePaginationControl(root, activeReviewList);
    nativeLoadMoreButton.click();
    remoteLoadTimer = window.setTimeout(() => {
      clearRemoteLoadRequest();
      scheduleEnhancement(root);
    }, remoteLoadTimeoutMs);
  }

  function bindPaginationControls(root) {
    root.addEventListener('click', (event) => {
      const viewMoreButton = event.target.closest('[data-nw-reviews-view-more]');
      if (viewMoreButton) {
        event.preventDefault();
        requestNextReviewBatch(root);
        return;
      }

      if (event.target.closest('.jdgm-subtab__name')) {
        window.setTimeout(() => resetActiveReviewBatch(root), 0);
      }
    });

    root.addEventListener('change', (event) => {
      if (event.target.matches('.jdgm-row-actions .jdgm-sort-dropdown')) {
        syncSortControlLabels(root);
        resetActiveReviewBatch(root);
      }
    });
  }

  function markTranslatedReview(translationButton) {
    const review = translationButton.closest('.jdgm-rev');
    if (!review) return;

    review.classList.add('nw-review--translated');
    review.classList.remove('nw-review--translation-pending', 'nw-review--translation-failed');
    review.querySelectorAll('.jdgm-rev__title, .jdgm-rev__body').forEach((element) => {
      element.setAttribute('lang', 'en');
    });
    translationAttempts.delete(translationButton);
  }

  function markTranslationFailed(translationButton) {
    const review = translationButton.closest('.jdgm-rev');
    if (!review) return;

    review.classList.add('nw-review--translation-failed');
    review.classList.remove('nw-review--translation-pending');
    translationAttempts.delete(translationButton);
  }

  function clearTranslationRetry() {
    if (translationRetryTimer === null) return;
    window.clearTimeout(translationRetryTimer);
    translationRetryTimer = null;
  }

  function clickTranslationButton(translationButton, root) {
    const attempts = translationAttempts.get(translationButton) || 0;
    if (attempts >= maxTranslationAttempts) {
      markTranslationFailed(translationButton);
      return false;
    }

    translationAttempts.set(translationButton, attempts + 1);
    activeTranslationButton = translationButton;
    translationButton.click();
    clearTranslationRetry();
    translationRetryTimer = window.setTimeout(() => {
      translationRetryTimer = null;
      scheduleEnhancement(root);
    }, translationRetryDelayMs);
    return true;
  }

  function clickVisibleTranslationBatch(translationButtons, root) {
    if (visibleTranslationBatchActive) return true;
    if (
      !translationButtons.length ||
      typeof window.jdgm?.autoTranslateReviewsIfAutomatic !== 'function' ||
      typeof window.jdgm?.$ !== 'function'
    ) {
      return false;
    }

    const reviews = Array.from(
      new Set(
        translationButtons
          .map((translationButton) => translationButton.closest('.jdgm-rev'))
          .filter(Boolean)
      )
    );
    const previousTranslationMode =
      window.jdgmSettings.widget_translate_review_content_method;
    let translationRequest;

    try {
      window.jdgmSettings.widget_translate_review_content_method = 'automatic';
      translationRequest = window.jdgm.autoTranslateReviewsIfAutomatic(
        window.jdgm.$(reviews)
      );
    } catch {
      return false;
    } finally {
      window.jdgmSettings.widget_translate_review_content_method =
        previousTranslationMode;
    }

    if (!translationRequest || typeof translationRequest.then !== 'function') {
      return false;
    }

    translationButtons.forEach((translationButton) => {
      const attempts = translationAttempts.get(translationButton) || 0;
      translationAttempts.set(translationButton, attempts + 1);
    });
    visibleTranslationBatchActive = true;
    Promise.resolve(translationRequest)
      .catch(() => undefined)
      .finally(() => {
        visibleTranslationBatchActive = false;
        scheduleEnhancement(root);
      });
    return true;
  }

  function translateNextReview(root) {
    root.querySelectorAll('.jdgm-translate-button').forEach((button) => {
      if (isTranslatedControlText(button.textContent)) markTranslatedReview(button);
    });

    if (activeTranslationButton?.isConnected) {
      const activeText = normalizeText(activeTranslationButton.textContent);

      if (activeText === translatingReviewText) return;
      if (isTranslatedControlText(activeText)) {
        markTranslatedReview(activeTranslationButton);
        clearTranslationRetry();
        activeTranslationButton = null;
      } else if (
        isTranslationTriggerText(activeText) &&
        translationRetryTimer === null
      ) {
        if (clickTranslationButton(activeTranslationButton, root)) return;
        activeTranslationButton = null;
      } else if (isTranslationTriggerText(activeText)) {
        return;
      } else {
        clearTranslationRetry();
        activeTranslationButton = null;
      }
    } else if (activeTranslationButton) {
      clearTranslationRetry();
      activeTranslationButton = null;
    }

    const visibleTranslationButtons = Array.from(
      root.querySelectorAll('.jdgm-translate-button')
    ).filter((button) => {
      const review = button.closest('.jdgm-rev');
      return (
        review &&
        !review.hidden &&
        !review.classList.contains('nw-review--batch-hidden') &&
        isTranslationTriggerText(button.textContent)
      );
    });

    visibleTranslationButtons.forEach((button) => {
      if ((translationAttempts.get(button) || 0) >= maxTranslationAttempts) {
        markTranslationFailed(button);
      }
    });
    const pendingTranslationButtons = visibleTranslationButtons.filter(
      (button) => (translationAttempts.get(button) || 0) < maxTranslationAttempts
    );

    if (!pendingTranslationButtons.length) return;
    if (clickVisibleTranslationBatch(pendingTranslationButtons, root)) return;

    clickTranslationButton(pendingTranslationButtons[0], root);
  }

  function hasStableWidgetStructure(widget) {
    return [
      '.jdgm-all-reviews__header',
      '.jdgm-subtab',
      '.jdgm-row-actions .jdgm-sort-dropdown',
      '.jdgm-all-reviews__body .jdgm-rev',
      '.jdgm-all-reviews__footer',
      '.jdgm-row-media',
    ].every((selector) => widget.querySelector(selector));
  }

  function hasPendingVisibleTranslations(root) {
    if (visibleTranslationBatchActive || activeTranslationButton?.isConnected) return true;

    return Array.from(root.querySelectorAll('.nw-review--translation-pending')).some(
      (review) => !review.hidden && !review.classList.contains('nw-review--batch-hidden')
    );
  }

  function revealEnhancedWidget(root, force = false) {
    const widget = root.querySelector('.jdgm-all-reviews-widget');
    if (!widget || widget.dataset.nwReviewsEnhanced === 'true') return;
    if (!force && (productCatalogState === 'idle' || productCatalogState === 'loading')) {
      return;
    }
    if (
      !shouldRevealReviewsWidget(
        hasStableWidgetStructure(widget),
        hasPendingVisibleTranslations(root),
        force
      )
    ) {
      return;
    }
    widget.dataset.nwReviewsEnhanced = 'true';
    if (initialWidgetRevealTimer !== null) {
      window.clearTimeout(initialWidgetRevealTimer);
      initialWidgetRevealTimer = null;
    }
  }

  function queueInitialWidgetRevealFallback(root) {
    if (initialWidgetRevealTimer !== null) return;
    initialWidgetRevealTimer = window.setTimeout(() => {
      initialWidgetRevealTimer = null;
      enhanceReviews(root);
      revealEnhancedWidget(root, true);
    }, initialWidgetRevealTimeoutMs);
  }

  function enhanceReviews(root) {
    formatReviewerNames(root);
    syncSortControlLabels(root);
    enhanceProductCards(root);
    prepareReviewPresentation(root);
    applyReviewBatches(root);
    translateNextReview(root);
    expandTruncatedReviews(root);
    revealEnhancedWidget(root);
  }

  function scheduleEnhancement(root) {
    queueInitialWidgetRevealFallback(root);
    if (enhancementScheduled) return;
    enhancementScheduled = true;

    window.requestAnimationFrame(() => {
      enhancementScheduled = false;
      enhanceReviews(root);
    });
  }

  function initializeReviewsPage() {
    const root = document.querySelector(rootSelector);
    if (!root) return;

    root.dataset.nwReviewsReady = 'true';
    bindPaginationControls(root);
    loadProductCatalog(root);
    const observer = new MutationObserver(() => scheduleEnhancement(root));
    observer.observe(root, { characterData: true, childList: true, subtree: true });
    scheduleEnhancement(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeReviewsPage, { once: true });
  } else {
    initializeReviewsPage();
  }
})();
